import { AnalyticsEventAction, AnalyticsEventType, PlausibleAnalyticsEventAction, PlausibleAnalyticsEventType } from '../hooks/analytics/analytics.types';
import { Logger } from './logger';

const logger = new Logger('Analytics');

const HDIS_ANALYTICS_COOKIE_NAME = 'hdis_ga';

/** @TOASK not only related to analytics */
export const getHdisAnalyticsCookieValue = () => {
  const matches = document.cookie.match(new RegExp(
    `(?:^|; )${HDIS_ANALYTICS_COOKIE_NAME.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`,
  ));

  const cookieValue = matches ? decodeURIComponent(matches[1]) : undefined;
  logger.debug(`cookie value of ${HDIS_ANALYTICS_COOKIE_NAME}:`, cookieValue);
  return cookieValue;
};

const setHdisAnalyticsCookieValue = (status: boolean) => {
  const expirationDate = new Date(Date.now() + 2592e+9).toUTCString(); // capped at 400 days (GA also sets the max expiration time)
  document.cookie = `${HDIS_ANALYTICS_COOKIE_NAME}=${status}; secure; samesite=strict; expires='max-age': ${expirationDate}`;
};

const removeHdisAnalyticsCookie = () => {
  const expirationDate = new Date(Date.now() - 1).toUTCString(); // expires instantly
  document.cookie = `${HDIS_ANALYTICS_COOKIE_NAME}=; secure; samesite=strict; expires='max-age': ${expirationDate}`;
};

const setGoogleAnalyticsConsent = (consent: boolean) => {
  /** @TOASK I need the check for gtag here because in preview and the current devlopment status it could be not defined */
  if (!window.gtag) {
    logger.warn('gtag is not defined');
    return;
  }

  if (consent) {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  } else {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }
};

export const enableAnalytics = () => {
  logger.debug('google analytics enabled');

  setGoogleAnalyticsConsent(true);
  setHdisAnalyticsCookieValue(true);
};

export const disableAnalytics = () => {
  logger.debug('google analytics disabled');

  setGoogleAnalyticsConsent(false);
  setHdisAnalyticsCookieValue(false);
};

export const unsetAnalyticsPreferences = () => {
  logger.debug('google analytics unset');

  setGoogleAnalyticsConsent(false);
  removeHdisAnalyticsCookie();
};

export function mapToPlausibleAnalyticsEventAction(eventAction: AnalyticsEventAction): PlausibleAnalyticsEventAction | null {
  switch (eventAction.type) {
    case AnalyticsEventType.LINK_TO_SPOTIFY: {
      return { type: PlausibleAnalyticsEventType.LINK_TO_SPOTIFY, tId: eventAction.track_id };
    }
    case AnalyticsEventType.TRACK_PLAY: {
      return { type: PlausibleAnalyticsEventType.TRACK_PLAY, tId: eventAction.track_id_current };
    }
    case AnalyticsEventType.TRACK_PAUSE: {
      return { type: PlausibleAnalyticsEventType.TRACK_PAUSE, tId: eventAction.track_id_current };
    }
    case AnalyticsEventType.TRACK_SEEK: {
      return { type: PlausibleAnalyticsEventType.TRACK_SEEK, tId: eventAction.track_id_current };
    }
    case AnalyticsEventType.GO_TO_NEXT_TRACK: {
      return { type: PlausibleAnalyticsEventType.GO_TO_NEXT_TRACK, tId: eventAction.track_id_target, tIdCurr: eventAction.track_id_current };
    }
    case AnalyticsEventType.GO_TO_PREVIOUS_TRACK: {
      return { type: PlausibleAnalyticsEventType.GO_TO_PREVIOUS_TRACK, tId: eventAction.track_id_target, tIdCurr: eventAction.track_id_current };
    }
    case AnalyticsEventType.GO_TO_TRACK: {
      return { type: PlausibleAnalyticsEventType.GO_TO_TRACK, tId: eventAction.track_id_target, tIdCurr: eventAction.track_id_current };
    }
    case AnalyticsEventType.SHOW_DESCRIPTION: {
      return { type: PlausibleAnalyticsEventType.SHOW_DESCRIPTION };
    }
    case AnalyticsEventType.SHOW_TRACKLIST: {
      return { type: PlausibleAnalyticsEventType.SHOW_TRACKLIST };
    }
    case AnalyticsEventType.SHOW_NOTES: {
      return { type: PlausibleAnalyticsEventType.SHOW_NOTES, tId: eventAction.track_id };
    }
    case AnalyticsEventType.PLAYER_ERROR: {
      return { type: PlausibleAnalyticsEventType.PLAYER_ERROR, err: eventAction.error, tId: eventAction?.track_id };
    }
    case AnalyticsEventType.PAGE_RELOAD: {
      return { type: PlausibleAnalyticsEventType.PAGE_RELOAD };
    }
    case AnalyticsEventType.USER_LOGIN: {
      return { type: PlausibleAnalyticsEventType.USER_LOGIN, user: eventAction.username };
    }
    case AnalyticsEventType.USER_LOGOUT: {
      return { type: PlausibleAnalyticsEventType.USER_LOGOUT, user: eventAction.username };
    }
    default: {
      console.debug('Unhandled Analytics Event type', eventAction);
      return null;
    }
  }
}
